import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { chip, table } from "../../ui/record";

// Appointments during care (brief 1E-19, mockup v79): on Home Care › Visits, the card listing each
// upcoming appointment that falls during a patient's visits, the visits it touches and how many
// caregivers answered; on the patient's Visits tab, one card per appointment with each caregiver's
// shift, what they're asked and their answer, and Ask again. Billing is unchanged by any of it.
// Shown only with the feature "Appointments during care" on.

interface Away {
  id: string;
  patientId: string | null;
  patient: string;
  when: string;
  where: string;
  appointment: string;
  touches: string;
  answers: string;
  answered: boolean;
  rows: { id: string; caregiver: string; shift: string; asked: string; answer: string | null }[];
}

/** Home Care › Visits: Appointments during care · n (empty with none, or the feature off). */
export async function appointmentsDuringCareCard(
  slot: HTMLElement,
  open: (patientId: string) => void,
) {
  const r = await api<{ on: boolean; list: Away[] }>("GET", "/api/appointments-during-care");
  if (!r.ok || !r.body.on || !r.body.list.length) {
    slot.replaceChildren();
    return;
  }
  slot.replaceChildren(
    el(
      "section",
      { class: "card", "aria-label": "Appointments during care" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("calendar"), ` Appointments during care · ${r.body.list.length}`),
          el("span", { class: "small muted" }, "Visits an appointment touches. Billing unchanged."),
        ),
      ),
      table(
        [
          { h: "Patient", f: (a: Away) => el("b", {}, a.patient) },
          { h: "Appointment", f: (a) => a.appointment },
          { h: "Visits it touches", f: (a) => el("span", { class: "small" }, a.touches) },
          { h: "Answers", f: (a) => chip(a.answers, a.answered ? "ok" : "warn") },
        ],
        r.body.list,
        (a) => a.patientId && open(a.patientId),
        "Appointments during care",
      ),
    ),
  );
}

/** The patient's Visits tab: one Appointment during care card per upcoming appointment. */
export async function patientAppointmentCards(slot: HTMLElement, patientId: string) {
  const r = await api<{ on: boolean; canAsk: boolean; list: Away[] }>(
    "GET",
    `/api/patients/${patientId}/appointments-during-care`,
  );
  if (!r.ok || !r.body.on) {
    slot.replaceChildren();
    return;
  }
  const draw = (list: Away[], canAsk: boolean) =>
    slot.replaceChildren(
      ...list.map((a) => {
        const again = el(
          "button",
          { type: "button", class: "btn sm" },
          icon("paper-airplane"),
          " Ask again",
        );
        again.addEventListener("click", async () => {
          again.disabled = true;
          const x = await api<{ list: Away[] }>(
            "POST",
            `/api/patients/${patientId}/appointments/${a.id}/ask-again`,
          );
          again.disabled = false;
          if (!x.ok) return toast("Nobody was asked", x.body.error);
          toast("Asked again", "Text and the field app");
          draw(x.body.list, canAsk);
        });
        return el(
          "section",
          { class: "card", "aria-label": "Appointment during care" },
          el(
            "div",
            { class: "cardhead" },
            el(
              "div",
              {},
              el("h2", {}, icon("calendar"), " Appointment during care"),
              el("span", { class: "small muted" }, `${a.when} · ${a.where}`),
            ),
            chip("Billing unchanged", "plain"),
          ),
          table(
            [
              { h: "Caregiver", f: (x: Away["rows"][number]) => el("b", {}, x.caregiver) },
              { h: "Shift", f: (x) => x.shift },
              { h: "Asked", f: (x) => el("span", { class: "small" }, x.asked) },
              {
                h: "Answer",
                f: (x) => (x.answer ? chip(x.answer, "ok") : chip("Not answered", "warn")),
              },
            ],
            a.rows,
            undefined,
            "Caregivers asked",
          ),
          el(
            "p",
            { class: "small muted", style: "margin:8px 0 0" },
            "Going with the patient is optional. Pay follows the clock.",
          ),
          canAsk && !a.answered ? el("div", { class: "actions" }, again) : null,
        );
      }),
    );
  draw(r.body.list, r.body.canAsk);
}
